import { Injectable, signal, computed } from '@angular/core';
import apiUserProfile, { TResponse as UserProfile } from '../../api/User/Profile';

export type { UserProfile };

@Injectable({
    providedIn: 'root',
})
export class UserProfileService {
    private profileSignal = signal<UserProfile | null>(null);
    private loadingSignal = signal<boolean>(false);
    private errorSignal = signal<string | null>(null);

    readonly profile = this.profileSignal.asReadonly();
    readonly loading = this.loadingSignal.asReadonly();
    readonly error = this.errorSignal.asReadonly();

    readonly isAuthenticated = computed(() => this.profileSignal() !== null);
    readonly currentUser = computed(() => this.profileSignal());
    readonly userRoles = computed(() => this.profileSignal()?.roles ?? []);
    readonly hospitalName = computed(() => {
        const profile = this.profileSignal();
        return profile?.roles.find((r) => r.roleCode === 'Cooperator')?.hospitalOrganizationName ?? '';
    });

    readonly currentServiceLocationCode = computed(() => {
        const profile = this.profileSignal();
        return profile?.roles.find((r) => r.roleCode === 'Cooperator')?.serviceLocationCode ?? '';
    });

    async getProfile(forceRefresh = false): Promise<UserProfile> {
        const cachedProfile = this.profileSignal();

        if (cachedProfile && !forceRefresh) {
            return cachedProfile;
        }

        if (this.loadingSignal()) {
            return this.waitForLoading();
        }

        try {
            this.loadingSignal.set(true);
            this.errorSignal.set(null);

            const response = await apiUserProfile();
            const profile = response.data;

            this.profileSignal.set(profile);
            return profile;
        }
        catch (error) {
            const errorMessage = error instanceof Error ? error.message : '獲取用戶資料失敗';
            this.errorSignal.set(errorMessage);
            throw error;
        }
        finally {
            this.loadingSignal.set(false);
        }
    }

    private async waitForLoading(): Promise<UserProfile> {
        while (this.loadingSignal()) {
            await new Promise((resolve) => setTimeout(resolve, 50));
        }

        const profile = this.profileSignal();
        if (!profile) {
            throw new Error('Failed to load profile');
        }

        return profile;
    }

    clearProfile(): void {
        this.profileSignal.set(null);
        this.errorSignal.set(null);
    }

    refreshProfile(): Promise<UserProfile> {
        return this.getProfile(true);
    }
}
